import { useState } from 'react';
import { Link } from 'react-router';
import { Badge, Button, Card, EmptyState, ErrorState, Heading, Spinner, Stack, Text, TextField } from '../../ui';
import { formatLabel, statusLabel, statusTone } from '../../racing/format';
import { useMyOrganizer, useMyRaceEvents, usePublicRaceEvents } from '../../racing/queries';
import { useCreateOrganizer } from '../../racing/mutations';
import type { RaceEventSummary } from '../../racing/types';

/**
 * The racing landing page: discovery of public race events, the caller's own hosted events, and the
 * prompt to become an organiser.
 *
 * @returns The page element.
 */
export function RaceListPage(): React.JSX.Element {
  const organizer = useMyOrganizer();
  const mine = useMyRaceEvents();
  const publicEvents = usePublicRaceEvents();

  const isOrganizer = organizer.data != null;

  return (
    <Stack gap="lg">
      <Stack direction="row" justify="space-between" align="center" wrap>
        <Heading level={1}>Racing</Heading>
        {isOrganizer && (
          <Link className="gm-button gm-button--primary" to="/racing/new">
            Host a race
          </Link>
        )}
      </Stack>

      {!organizer.isPending && !isOrganizer && <BecomeOrganizer />}

      {isOrganizer && (
        <section aria-label="Your events">
          <Heading level={2}>Your events</Heading>
          {mine.isPending && <Spinner label="Loading your events" />}
          {mine.isError && <ErrorState message={mine.error.message} onRetry={() => void mine.refetch()} />}
          {!mine.isPending && !mine.isError && mine.data.length === 0 && (
            <Text muted>You are not hosting any races yet. Host one to build a bracket and score it live.</Text>
          )}
          {!mine.isPending && !mine.isError && mine.data.length > 0 && (
            <div className="gm-race-list">
              {mine.data.map((event) => (
                <RaceEventCard key={event.id} event={event} />
              ))}
            </div>
          )}
        </section>
      )}

      <section aria-label="Discover races">
        <Heading level={2}>Discover</Heading>
        {publicEvents.isPending && (
          <Stack gap="md" align="center">
            <Spinner label="Loading races" />
            <Text muted>Finding races…</Text>
          </Stack>
        )}
        {publicEvents.isError && (
          <ErrorState message={publicEvents.error.message} onRetry={() => void publicEvents.refetch()} />
        )}
        {!publicEvents.isPending && !publicEvents.isError && publicEvents.data.length === 0 && (
          <EmptyState
            title="No public races yet"
            description="Once an organiser opens a race for registration, it will show up here."
          />
        )}
        {!publicEvents.isPending && !publicEvents.isError && publicEvents.data.length > 0 && (
          <div className="gm-race-list">
            {publicEvents.data.map((event) => (
              <RaceEventCard key={event.id} event={event} />
            ))}
          </div>
        )}
      </section>
    </Stack>
  );
}

/** The inline prompt and form for creating an organiser profile. */
function BecomeOrganizer(): React.JSX.Element {
  const [name, setName] = useState('');
  const create = useCreateOrganizer();

  return (
    <Card ariaLabel="Become an organiser">
      <Stack gap="sm">
        <Heading level={3}>Host your own races</Heading>
        <Text muted>Create an organiser profile to host race events, build brackets and score them live.</Text>
        <Stack direction="row" gap="sm" align="end" wrap>
          <TextField
            label="Organiser name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            error={create.isError ? create.error.message : undefined}
          />
          <Button
            variant="primary"
            disabled={name.trim() === '' || create.isPending}
            onClick={() => create.mutate({ name: name.trim() })}
          >
            {create.isPending ? 'Creating…' : 'Create profile'}
          </Button>
        </Stack>
      </Stack>
    </Card>
  );
}

/** One race event summary card, linking to its detail page. */
function RaceEventCard({ event }: { readonly event: RaceEventSummary }): React.JSX.Element {
  return (
    <Link className="gm-race-card" to={`/racing/${event.id}`}>
      <Stack gap="sm">
        <Stack direction="row" justify="space-between" align="center" wrap>
          <Heading level={3}>{event.title}</Heading>
          <Badge tone={statusTone(event.status)}>{statusLabel(event.status)}</Badge>
        </Stack>
        <Text muted>{formatLabel(event.format)}</Text>
        <Stack direction="row" gap="sm" wrap>
          <Badge tone="neutral">{event.contestantCount} contestants</Badge>
          {event.organizerName != null && <Text muted>{event.organizerName}</Text>}
        </Stack>
      </Stack>
    </Link>
  );
}
